import { Component, computed, inject, input, output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { LocationsTableComponent } from '@app/+authenticated/+account/settings/+locations/locations-table/locations-table.component';
import { getActiveDepartmentAmount, getActiveDepartments } from '@app/reducers/orm/department/department.service';
import { getActiveTeams } from '@app/reducers/orm/team/team.service';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { ButtonComponent } from '@sb/ui/button';
import { CardComponent } from '@sb/ui/card';
import { PlanQuotaModel, QuotaType } from '@shiftbase-com/ts-shared/models';

import { QuotaCardRuleComponent } from '../quota-card-rule/quota-card-rule.component';

@Component({
  selector: 'app-downgrade-locations',
  imports: [
    TranslatePipe,
    LocationsTableComponent,
    QuotaCardRuleComponent,
    ButtonComponent,
    RouterModule,
    CardComponent,
  ],
  templateUrl: './downgrade-locations.component.html',
})
export class DowngradeLocationsComponent {
  #store = inject<Store<AppState>>(Store);

  subscriptionPlanQuotas = input.required<Partial<Record<QuotaType, PlanQuotaModel>>>();

  planName = input<string>();

  // Emitted when departments/teams fit the plan and the user continues. The wizard decides whether
  // to advance to the next step or finalize the downgrade — this step no longer owns that.
  next = output<void>();

  quotaType = QuotaType;

  // Usage is read from the live store (not the subscription snapshot) so the cards, the overage hint,
  // and the Continue gate update immediately as departments/teams are removed in the table below.
  #departmentsUsage = this.#store.selectSignal(getActiveDepartmentAmount);
  #activeTeams = this.#store.selectSignal(getActiveTeams);
  #activeDepartments = this.#store.selectSignal(getActiveDepartments);

  // Match the backend TEAMS quota (countWithinActiveDepartments): count non-deleted teams that live
  // in an active (non-deleted) department. This excludes teams left behind by a deactivated department
  // and archived teams, so the count drops as the user removes departments/teams below.
  #teamsUsage = computed(() => {
    const activeDepartmentIds = new Set(this.#activeDepartments().map((department) => department.id));

    return this.#activeTeams().filter((team) => activeDepartmentIds.has(team.department_id)).length;
  });

  // Cards/gate compare live usage against the target plan's limits (provided via the quota input).
  quotas = computed<Partial<Record<QuotaType, PlanQuotaModel>>>(() => ({
    ...this.#liveQuota(QuotaType.DEPARTMENTS, this.#departmentsUsage()),
    ...this.#liveQuota(QuotaType.TEAMS, this.#teamsUsage()),
  }));

  departmentsOverBy = computed(() => this.#overBy(this.quotas()[QuotaType.DEPARTMENTS]));

  teamsOverBy = computed(() => this.#overBy(this.quotas()[QuotaType.TEAMS]));

  isOverQuota = computed(() => this.departmentsOverBy() > 0 || this.teamsOverBy() > 0);

  #liveQuota(quotaType: QuotaType, usage: number): Partial<Record<QuotaType, PlanQuotaModel>> {
    const limit = this.subscriptionPlanQuotas()[quotaType]?.limit;

    if (limit === undefined) {
      return {};
    }

    return { [quotaType]: { name: quotaType, type: quotaType, usage, limit } };
  }

  #overBy(quota: PlanQuotaModel | undefined): number {
    if (!quota) {
      return 0;
    }

    return Math.max(0, quota.usage - quota.limit);
  }

  continue(): void {
    this.next.emit();
  }
}
